import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { withTransaction } from '@datorama/akita';
import { filter, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { LegalEntity, OwnerStatus } from './legal-entity.model';
import { LegalEntityStore } from './legal-entity-store.service';
import { AppConfigQuery } from '../../../../core/state/app-config/app-config.query';
import { Observable } from 'rxjs';
import { Cacheable } from 'ts-cacheable';
import { UserSettingsQuery } from '../../../../features/user-settings/state/user-settings.query';
import { UserSettingsService } from '../../../../features/user-settings/state/user-settings.service';

@Injectable({ providedIn: 'root' })
export class LegalEntityService {
    constructor(
        private store: LegalEntityStore,
        private http: HttpClient,
        private config: AppConfigQuery,
        private _userSettingsQuery: UserSettingsQuery,
        private _userSettingsService: UserSettingsService
    ) {}

    /** _fetchLegalEntities
     * @description Fetch all legal entities from the server, response is cached for 1 second.
     * @private
     * @returns - observable for request
     */
    @Cacheable({
        maxAge: 1000
    })
    private _fetchLegalEntities() {
        return this.http.get<LegalEntity[]>(
            `${this.config.get().adminBaseUrl}/v1/owners`
        );
    }

    /** get
     * @description Get all legal entities from the server and set them in the store.
     * @returns - observable for request
     */
    get() {
        return this._fetchLegalEntities().pipe(
            withLatestFrom(
                this._userSettingsQuery.isLoading$.pipe(
                    filter((isLoading) => isLoading === false),
                    take(1),
                    switchMap((_) =>
                        this._userSettingsQuery.selectGlobalSetting$(
                            'SELECTED_LEGAL_ENTITIES'
                        )
                    )
                )
            ),
            withTransaction(([entities, persistedSelection]) => {
                this.store.set(entities);
                // if no legal entities are selected...
                if (
                    this.store.getValue().active.length === 0 &&
                    persistedSelection.length === 0
                ) {
                    // ...select all legal entities by default
                    this.setSelectedState(entities, true);
                } else if (persistedSelection.length > 0) {
                    // ...otherwise select the persisted ones
                    this.setSelectedIds(persistedSelection);
                }
            })
        );
    }

    /** Get single owner by id.
     * @param id - owner id
     * @returns - observable for request
     */
    getById(id: LegalEntity['ownerId']) {
        return this.http
            .get<LegalEntity>(`${this.config.get().adminBaseUrl}/v1/owners/${id}`)
            .pipe(
                tap((owner) => {
                    this.store.upsert([owner.ownerId], owner);
                })
            );
    }

    /** Create new owner - create a new owner.
     * @param data - new owner data.
     * @returns - observable for request
     */
    create(data: Partial<LegalEntity>) {
        return this.http
            .post<LegalEntity>(`${this.config.get().adminBaseUrl}/v1/owners`, data)
            .pipe(
                tap((owner) =>
                    this.store.add(owner, {
                        prepend: true
                    })
                )
            );
    }

    /** Update - change owner details
     * @param data -
     * @param id -
     * @returns - observable for request
     */
    update(data: LegalEntity, id: LegalEntity['ownerId']) {
        return this.http
            .put<LegalEntity>(`${this.config.get().adminBaseUrl}/v1/owners/${id}`, data)
            .pipe(tap((owner) => this.store.upsert(owner.ownerId, owner)));
    }

    upsert(data: LegalEntity): Observable<LegalEntity> {
        if (data.ownerId) {
            return this.update(data, data.ownerId);
        }
        return this.create(data);
    }

    /** Delete an owner from the system.
     * @param id - owner id
     * @returns - observable for request
     */
    delete(id: LegalEntity['ownerId']) {
        return this.http
            .delete<LegalEntity>(`${this.config.get().adminBaseUrl}/v1/owners/${id}`)
            .pipe(tap(() => this.store.remove(id)));
    }

    /** Disable/Enable (acts as a soft-delete) an owner from the system.
     * @param id - account issuer id
     * @param status - describes the wanted status to be set for the entitiy
     * @returns - observable for request
     */
    toggleEnabled(owner: LegalEntity) {
        return this.http
            .put<LegalEntity>(
                `${this.config.get().adminBaseUrl}/v1/owners/${owner.ownerId}`,
                {
                    ...owner,
                    status:
                        owner.status === OwnerStatus.ENABLED
                            ? OwnerStatus.DISABLED
                            : OwnerStatus.ENABLED
                }
            )
            .pipe(tap((o) => this.store.update(o.ownerId, o)));
    }

    /** toggleSelectedState
     * @description Toggle selected (akita active) state of a legal entity, update active ids.
     * @param legalEntity - legal entity to toggle
     * @param persist - should the selected state be persisted to user settings
     */
    toggleSelectedState(legalEntity: LegalEntity, persist: boolean = true) {
        this.store.toggleActive(legalEntity.ownerId);
        if (persist) {
            this._persistSelectedState();
        }
    }

    /** setSelectedState
     * @description Set selected (akita active) state of one or more legal entities update active ids.
     * @param legalEntity - legal entity or entities to set selected state for
     * @param selected - selected state
     * @param persist - should the selected state be persisted to user settings
     */
    setSelectedState(
        legalEntity: LegalEntity | Array<LegalEntity>,
        selected: boolean,
        persist: boolean = true
    ) {
        const legalEntityIds = Array.isArray(legalEntity)
            ? legalEntity
                  .filter((entity) => entity.metadata?.includeInGlobalFilter !== false)
                  .map((entity) => entity.ownerId)
            : [legalEntity.ownerId];
        if (selected) {
            this.store.setActive(legalEntityIds);
        } else {
            this.store.removeActive(legalEntityIds);
        }
        if (persist) {
            this._persistSelectedState();
        }
    }

    /** setSelectedIds
     * @description Set selected (akita active) state of one or more legal entities update active ids.
     * @param ids - legal entity ids to set selected state for
     */
    setSelectedIds(ids: Array<LegalEntity['ownerId']>) {
        this.store.setActive(ids);
    }

    /** _persistSelectedState
     * @description Persist selected state of legal entities to user settings.
     * @private
     */
    private _persistSelectedState() {
        // if no legal entities are selected...
        if (this.store.getValue().active.length === 0) {
            // ...don't persist the state as we don't want to save an empty state
            return;
        }
        // check if all legal entities that can be selected are selected
        const allSelected =
            this.store.getValue().active.length ===
            Object.values(this.store.getValue().entities).filter(
                (entity) => entity.metadata?.includeInGlobalFilter !== false
            ).length;

        // if all legal entities are selected...
        if (allSelected) {
            // ...we don't need to persist the selected state as all are selected by default, hence we remove the persisted state.
            // This behavior allows newly created legal entities to be selected by default
            this._userSettingsService
                .removeGlobalSetting('SELECTED_LEGAL_ENTITIES')
                .subscribe();
        } else {
            // ...otherwise we persist the selected ones
            this._userSettingsService
                .saveGlobalSetting(
                    'SELECTED_LEGAL_ENTITIES',
                    this.store.getValue().active
                )
                .subscribe();
        }
    }
}
